<template>
  <div class="content">
    <!-- 操作 -->
    <div class="operate-group">
      <div class="operate-group-left"></div>
      <div class="operate-group-right">
        <search-toolbar
          v-model:quick-field="quickSearchField"
          v-model:quick-value="quickSearchValue"
          :quick-fields="quickSearchFields"
          :default-quick-field="defaultQuickField"
          :search-mode="searchMode"
          :filter-summary="filterStr"
          :advanced-button-text="advancedFilterButtonText"
          :choose-placeholder="t('placeholder.choose')"
          :search-placeholder="t('placeholder.search')"
          :refresh-text="t('table.refresh')"
          @quick-field-change="handleQuickFieldChange"
          @quick-value-change="handleQuickValueChange"
          @quick-input="handleQuickInput"
          @open-advanced="openAdvancedDrawer"
          @reset-search="resetAllSearch"
          @refresh="getList" />
      </div>
    </div>
    <!-- 表格 -->
    <custom-table :cellClassName="cellClassName" :cellClick="cellClick" :tableData="tableData" :height="$store.getters.windowHeight - 260 < 200 ? 200 : $store.getters.windowHeight - 260" @getTableData="getList" />
    <!-- 详情 -->
    <el-drawer
      v-model="visibleDetail"
      :close-on-click-modal="true"
      :destroy-on-close="true"
      :title="t('route.log_detail')"
      custom-class="host-detail-drawer"
      append-to-body
      size="60%">
      <Detail ref="detail" />
    </el-drawer>
    <advanced-filter-drawer
      v-model:visible="advancedDrawerVisible"
      :title="advancedFilterButtonText"
      width="50%"
      :loading="tenantOptionsLoading"
      :has-other-condition="false"
      :filter-condition="advancedFilterCondition"
      :other-condition-list="advancedOtherConditionList"
      @submit="handleAdvancedSubmit"
      @cancel="handleAdvancedCancel" />
  </div>
</template>

<script setup>
import { reactive, watch, ref, nextTick, computed } from 'vue'
import AdvancedFilterDrawer from '@/components/AdvancedFilterDrawer'
import SearchToolbar from '@/components/SearchToolbar'
import { getLogList } from '@/api/event/list'
import { getResourceTypes } from '@/api/alarmAdmin/alarm'
import { getTenants } from '@/api/users/tenant'
import { sceneOptions } from '@/dataJson/lisense'
import getTableList from '@/composables/getList' // 获取列表
import useListSearch from '@/composables/useListSearch'
import { useI18n } from 'vue-i18n'
import Detail from './detail.vue'
const detail = ref()
const { t } = useI18n()
const sceneOption = sceneOptions // 场景
// 定义搜索字段
const search = reactive({
  project_id: '',
  event_message_id: '',
  resource_type_id: '',
  scene: '',
  resource: ''
})
// 获取资源类型
const resourceOptions = ref([])
const getResourceFun = async(info) => {
  const res = await getResourceTypes(info)
  resourceOptions.value = res.data.resource_types
}
const visibleDetail = ref(false)
// 获取列表
const { listData, listTotal, getList, searchFun } = getTableList(search, getLogList, { data: 'event_messages', total: 'total' })
const defaultQuickField = 'resource'
const tenantOptions = ref([])
const tenantOptionsLoading = ref(false)
let tenantOptionsPromise = null

const normalizeOptions = (options = []) => {
  return options.map(item => ({
    label: item.label ?? item.display_name ?? item.name ?? '',
    value: item.value ?? item.id ?? ''
  }))
}

const loadTenantOptions = async() => {
  if (tenantOptionsPromise) return tenantOptionsPromise

  tenantOptionsLoading.value = true
  tenantOptionsPromise = (async() => {
    try {
      const res = await getTenants({
        scene: null,
        page: 1,
        page_size: 99999
      })
      tenantOptions.value = res.data.pages.map(item => ({
        label: `${item.name}(${item.id})`,
        value: item.id
      }))
    } catch (error) {
      tenantOptions.value = []
    } finally {
      tenantOptionsLoading.value = false
      tenantOptionsPromise = null
    }
  })()

  return tenantOptionsPromise
}

const createLogSearchFields = () => {
  return {
    resource: {
      key: 'resource',
      label: t('alarm.operation_resource'),
      filterType: 'filter',
      type: 'input',
      value: '',
      defaultValue: '',
      trim: true,
      placeholder: t('placeholder.search'),
      clearable: true
    },
    event_message_id: {
      key: 'event_message_id',
      label: t('alarm.event_id'),
      filterType: 'filter',
      type: 'input',
      value: '',
      defaultValue: '',
      trim: true,
      placeholder: t('placeholder.search'),
      clearable: true
    },
    scene: {
      key: 'scene',
      label: t('license.scene'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: normalizeOptions([
        { label: t('table.all'), value: '' },
        ...sceneOption,
        { label: t('log.background'), value: 'system' }
      ]),
      placeholder: t('placeholder.choose'),
      clearable: true
    },
    resource_type_id: {
      key: 'resource_type_id',
      label: t('alarm.resource_type'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: normalizeOptions([
        { label: t('table.all'), value: '' },
        ...resourceOptions.value
      ]),
      placeholder: t('placeholder.choose'),
      clearable: true
    },
    project_id: {
      key: 'project_id',
      label: t('user.tenant'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: normalizeOptions(tenantOptions.value),
      placeholder: t('placeholder.choose'),
      clearable: true,
      filterable: true
    }
  }
}

const quickSearchFields = computed(() => createLogSearchFields())
const advancedFilterButtonText = computed(() => t('table.advancedFilter'))
const {
  searchMode,
  quickSearchField,
  quickSearchValue,
  advancedDrawerVisible,
  filterStr,
  advancedFilterCondition,
  resetAllSearch,
  handleQuickFieldChange,
  handleQuickValueChange,
  handleQuickInput,
  openAdvancedDrawer,
  handleAdvancedSubmit,
  handleAdvancedCancel
} = useListSearch({
  search,
  searchFun,
  quickFields: quickSearchFields,
  defaultQuickField,
  createAdvancedFilterCondition: createLogSearchFields,
  onQuickFieldChange: async(field) => {
    if (field === 'project_id') await loadTenantOptions()
  },
  onOpenAdvanced: loadTenantOptions
})
const advancedOtherConditionList = computed(() => ({}))
// table data
const tableData = reactive({
  header: [
    {
      key: 'display_name',
      value: t('alarm.operation_description'),
      fixed: 'left',
      minWidth: '190px',
      sortable: true
    },
    {
      key: 'resource',
      value: t('alarm.operation_resource'),
      minWidth: '190px',
      sortable: true
    },
    {
      key: 'display_resource_type',
      value: t('alarm.resource_type'),
      minWidth: '160px',
      sortable: true
    },
    {
      key: 'display_result',
      minWidth: '160px',
      value: t('alarm.operation_result'),
      sortable: true,
      isShowCircle: true,
      icon: 'result',
      abnormalValue: (row) => row.display_result
    },
    {
      minWidth: '140px',
      key: 'display_scene',
      value: t('license.scene'),
      sortable: true
    },
    {
      key: 'username',
      minWidth: '140px',
      value: t('alarm.operator'),
      sortable: true
    },
    {
      key: 'enterprise_name',
      minWidth: '140px',
      value: t('log.belongs'),
      sortable: true
    },
    {
      key: 'remote_ip',
      value: t('log.remote_ip'),
      minWidth: '140px',
      sortable: true
    },
    {
      key: 'description',
      value: t('table.description'),
      minWidth: '140px',
      sortable: true
    },
    {
      key: 'occurred_at',
      value: t('table.occurred_at'),
      minWidth: '160px',
      sortable: true
    }
  ],
  data: listData,
  total: listTotal
})
// 单元格class
const cellClassName = (row) => {
  if (row.column.rawColumnKey === 'display_name') {
    return 'isClick'
  }
}
// 点击查看详情
const cellClick = async(row, column) => {
  if (column.rawColumnKey === 'display_name') {
    visibleDetail.value = true
    await nextTick()
    detail.value.getDetailFun(row.id)
    // router.push({
    //   name: 'logDetail',
    //   params: {
    //     name: row.display_name,
    //     id: row.id
    //   }
    // })
  }
}
watch(
  () => search.scene,
  (val) => {
    if (val === '') {
      resourceOptions.value = []
    } else {
      getResourceFun({ scene: val })
    }
  },
  {
    immediate: true
  }
)
</script>

<style lang="scss" scoped>
.operate-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 16px;
}
.operate-group-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.operate-group-right {
  margin-top: 0 !important;
  margin-left: auto;
  flex-wrap: nowrap !important;
}
.operate-group-right :deep(.search-toolbar) {
  flex-wrap: nowrap;
}
</style>
